// cruiseDetails — the ONE place the internal app read a cruise's sailing times and
// the boarding point from.
//
// The printed ticket, the WhatsApp ticket send, the check-in cruise picker
// and the two settings screens go through these three functions. They take the CRUISE DATE
// the value is for: a change to the times or the boarding point is scheduled
// from a chosen date (cruise_schedule_changes / boarding_point_changes), and
// the value that applies is the one in force on the day the guest sails,
// never the day they booked.
//
// Resolution happens in ONE database function, cruise_details_on(code, name,
// date): the scheduled value if a change is in force on that date, else the
// standing cruise_settings / ticket_settings row. If that call fails for any
// reason, these functions fall back to reading the standing rows directly, so
// a reader always gets a value; each caller then keeps its own formatting and
// its own hardcoded last-resort text, so nothing is ever blank.
//
// Return shapes are the raw DB columns (HH:MM 24-hour text, raw boarding
// point) or null when nothing was found.
//
// `date` may be an ISO 'YYYY-MM-DD' (the booking flow's selectedDate) or the
// stored 'DD-MM-YYYY' (bookings.date); null when no date applies, in which
// case the standing rows are returned.

import { supabase } from '../lib/supabase';

const TIME_COLUMNS = 'departure_time, end_time, reporting_time';

/** ISO or DD-MM-YYYY → ISO 'YYYY-MM-DD'; anything else → null. */
export function toIsoDate(date) {
  const s = String(date ?? '').trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(s);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return null;
}

function pickTimes(row) {
  return {
    departure_time: row.departure_time ?? null,
    end_time: row.end_time ?? null,
    reporting_time: row.reporting_time ?? null,
  };
}

// One call to the resolver; null on any error so the caller can fall back.
async function detailsOn({ cruiseCode = null, cruiseName = null, date }) {
  const { data, error } = await supabase.rpc('cruise_details_on', {
    p_cruise_code: cruiseCode || null,
    p_cruise_name: cruiseName || null,
    p_date: toIsoDate(date),
  });
  if (error || !Array.isArray(data)) return null;
  return data;
}

/**
 * Sailing times for one cruise, by code or by name, in force on `date`.
 * @returns {{ departure_time: string|null, end_time: string|null, reporting_time: string|null } | null}
 */
export async function fetchCruiseTimes({ cruiseCode, cruiseName, date }) {
  if (!cruiseCode && !cruiseName) return null;

  const rows = await detailsOn({ cruiseCode, cruiseName, date });
  if (rows) return rows.length ? pickTimes(rows[0]) : null;

  // Fallback: the standing row.
  let q = supabase.from('cruise_settings').select(TIME_COLUMNS);
  q = cruiseCode ? q.eq('cruise_code', cruiseCode) : q.eq('cruise_name', cruiseName);
  const { data, error } = await q.maybeSingle();
  if (error || !data) return null;
  return pickTimes(data);
}

/**
 * Sailing times for every cruise (active or not) in force on `date`, keyed by cruise_name.
 * @returns {Record<string, { cruise_code: string|null, departure_time: string|null, end_time: string|null, reporting_time: string|null }>}
 */
export async function fetchAllCruiseTimes({ date }) {
  let rows = await detailsOn({ date });
  if (!rows) {
    // Fallback: the standing rows.
    const { data, error } = await supabase
      .from('cruise_settings')
      .select(`cruise_name, cruise_code, ${TIME_COLUMNS}`);
    rows = (error || !data) ? [] : data;
  }
  const byName = {};
  for (const row of rows) {
    byName[row.cruise_name] = { cruise_code: row.cruise_code ?? null, ...pickTimes(row) };
  }
  return byName;
}

/**
 * The boarding point (one for all cruises) and its map link, in force on `date`.
 * @returns {{ boarding_point: string|null, boarding_maps_url: string|null } | null}
 */
export async function fetchBoardingPoint({ date }) {
  const { data, error } = await supabase.rpc('boarding_point_on', { p_date: toIsoDate(date) });
  if (!error && Array.isArray(data) && data.length) {
    return {
      boarding_point: data[0].boarding_point ?? null,
      boarding_maps_url: data[0].boarding_maps_url ?? null,
    };
  }

  // Fallback: the standing row.
  const { data: ts, error: tsErr } = await supabase
    .from('ticket_settings')
    .select('boarding_point, boarding_maps_url')
    .limit(1)
    .maybeSingle();
  if (tsErr || !ts) return null;
  return {
    boarding_point: ts.boarding_point ?? null,
    boarding_maps_url: ts.boarding_maps_url ?? null,
  };
}
